import { appendQueryToUrlSearchParams, buildUrl } from '../utils';
// PageNavigation helpers

/**
 * Important!: total ratings is not always accurate, that is why we need to use
 * currReviewsInPage to check if the current page is the last page
 * checkIfLastPage
 * @param {number} page - current page number
 * @param {number} maxReviewsPerPage - max reviews per page
 * @param {number} currReviewsInPage - current reviews in page
 * @param {number} totalRatings - total ratings
 * @returns {boolean} true if current page is the last page or if the current page has less reviews than the max reviews per page
 */
export const checkIfLastPage = (
	page,
	maxReviewsPerPage,
	currReviewsInPage,
	totalRatings
) => {
	const lastPage = Math.ceil(totalRatings / maxReviewsPerPage);
	// true if current page is the last page or
	// if the current page has less reviews than the max reviews per page
	return page === lastPage || currReviewsInPage < maxReviewsPerPage;
};

/**
 * generatePageList
 * @param {number} currentPage - current page number
 * @param {number} totalPages - total pages
 * @returns {array} array of page numbers and ellipsis
 * @description
 * Generates an array of page numbers to display in the page navigation
 * If there are 5 or fewer pages, just show them all
 * If the current page is more than 3, add an ellipsis after the first page
 * If the current page is not near the first or last page (i.e., it's not page 2, 3, second last, or third last),
 * add the page before, the current page, and the page after
 * If the current page is near the start (i.e., it's page 2 or 3), add those pages
 * If the current page is page 3, add page 4
 * If the current page is near the end (i.e., it's second last or third last), add those pages
 * If the current page is the third last page, add fourth last page
 * If the current page is less than the third last page, add an ellipsis before the last page
 * Always include the last page
 * @example
 * generatePageList(1, 5) // [1, 2, 3, 4, 5]
 * generatePageList(1, 10) // [1, 2, 3, '...', 10]
 * generatePageList(4, 10) // [1, '...', 3, 4, 5, '...', 10]
 * generatePageList(8, 10) // [1, '...', 7, 8, 9, 10]
 */
export const generatePageList = (currentPage, totalPages) => {
	let pages = [];

	if (totalPages <= 5) {
		// If there are 5 or fewer pages, just show them all
		pages = Array.from({ length: totalPages }, (_, i) => i + 1);
	} else {
		// Always include the first page
		pages.push(1);

		// If the current page is higher than 3, add an ellipsis after the first page
		if (currentPage > 3) {
			pages.push('...');
		}

		// If the current page is not near the first or last page (i.e., it's not page 2, 3, second last, or third last),
		// add the page before, the current page, and the page after
		if (currentPage > 3 && currentPage < totalPages - 2) {
			pages.push(currentPage - 1, currentPage, currentPage + 1);
		}

		// If the current page is near the start (i.e., it's page 2 or 3), add those pages
		if (currentPage <= 3) {
			pages.push(2, 3);
			// If the current page is page 3, add page 4
			if (currentPage === 3) pages.push(4);
		}

		// If the current page is near the end (i.e., it's second last or third last), add those pages
		if (currentPage >= totalPages - 2) {
			// If the current page is the third last page, add fourth last page
			if (currentPage === totalPages - 2) {
				pages.push(totalPages - 3);
			}
			// Always include the second last and third last pages
			pages.push(totalPages - 2, totalPages - 1);
		}

		// If the current page is less than the third last page, add an ellipsis before the last page
		if (currentPage < totalPages - 2) {
			pages.push('...');
		}

		// Always include the last page
		pages.push(totalPages);
	}

	return pages;
};

// state helpers

/**
 * Returns an object with key value pairs, the value is boolean
 * @param {array} reviewsFilters - Array of strings
 * @param {boolean} [value] - Boolean
 * @returns {GenericState} filterState - Object with key value pairs, the value is boolean
 * @example
 * const reviewsFilters = ['1', '2', '3', '4', '5'];
 * const filtersState = stringArrayToState(reviewsFilters);
 * console.log(filtersState);
 * // {
 * // 	1: false,
 * // 	2: false,
 * // 	3: false,
 * // 	4: false,
 * // 	5: false
 * // }
 */
export const stringArrayToState = (reviewsFilters, value = false) => {
	const filtersState = {};
	reviewsFilters.reduce((acc, filter) => {
		acc[filter] = value;
		return acc;
	}, filtersState);
	return filtersState;
};

/**
 * Returns an object with key value pairs
 * @param {array} ratings - Array of strings
 * @param {array} models - Array of strings
 * @param {array} types - Array of strings
 * @param {string} defaultType - String
 * @param {boolean} [value] - Boolean
 * @returns {FiltersState} - Object with key value pairs
 */
export const buildFiltersState = (
	ratings,
	models,
	types,
	defaultType = 'overall',
	value = false
) => {
	return {
		ratings: stringArrayToState(ratings, value),
		models: stringArrayToState(models, value),
		types: {
			options: types,
			selected: defaultType
		}
	};
};

/**
 * Returns an array of strings
 * @param {GenericState} state - Object with key value pairs, the value is boolean
 * @returns {Array<string>} - Array of strings
 * @example
 * const state = {
 * 	1: false,
 * 	2: false,
 * 	3: false,
 * 	4: true,
 * 	5: true
 * };
 * const stringArray = stateToStringArray(state);
 * console.log(stringArray); // ['4', '5']
 */
export const stateToStringArray = (state) => {
	return Object.entries(state)
		.filter(([, checked]) => checked)
		.map(([key]) => key);
};

/**
 * Returns the url to fetch reviews
 * @param {string} path - path to fetch reviews
 * @param {number} page - current page number
 * @param {FiltersState} state - Object with key value pairs
 * @param {string} [host] - host
 * @param {string} [dealerId] - dealer id (USAV + dealer code)
 * @param {number} [reviewsPerPage] - max reviews per page
 * @returns {URL} url - url to fetch reviews
 */
export const stateToUrl = (
	path,
	page,
	state,
	host,
	dealerId,
	reviewsPerPage
) => {
	return buildUrl(
		path,
		{
			ratings: stateToStringArray(state.ratings).sort(),
			models: stateToStringArray(state.models).sort(),
			types: state.types.selected,
			page,
			vwDealerId: dealerId,
			maxSize: reviewsPerPage
		},
		host
	);
};

/**
 * Creates a URLSearchParams object from the state
 * @param {FiltersState} state - Object with key value pairs
 * @param {QueryObject} [queryObj] - Object of queries
 * @returns {URLSearchParams} searchParams - URLSearchParams object
 */
export const stateToSearchParams = (state, queryObj) => {
	const searchParams = new URLSearchParams();
	appendQueryToUrlSearchParams(searchParams, {
		ratings: stateToStringArray(state.ratings).sort(),
		models: stateToStringArray(state.models).sort(),
		types: state.types.selected,
		...queryObj
	});
	return searchParams;
};

// server-side rendering helpers

/**
 * Returns the dealer code from the dealer id
 * @param {string} dealerCode
 * @returns {string} dealerId - (USAV + dealer code)
 * @example
 * const dealerId = '123456';
 * const dealerCode = dealerIdToDealerCode(dealerId);
 * console.log(dealerCode); // 'USAV123456'
 */
export const dealerCodeToDealerId = (dealerCode) => {
	return `USAV${dealerCode}`;
};

/**
 * Returns the valid ratings, models, and types if there is any
 * @param {QueryObject} parameterMap - Object of queries
 * @param {Array<number>} validRatings - Array of numbers
 * @param {Array<string>} validModels - Array of strings
 * @param {Array<string>} validTypes - Array of strings
 * @returns {FiltersState} - Object with key value pairs
 */
export const filterParameterMap = (
	parameterMap,
	validRatings,
	validModels,
	validTypes
) => {
	const rawTypes = parameterMap.types ?? 'overall';
	// Filter out invalid types
	const types = validTypes.includes(rawTypes) ? rawTypes : 'overall';
	let rawRatings = parameterMap['ratings[]'] ?? [];
	rawRatings = Array.isArray(rawRatings) ? rawRatings : [rawRatings];
	// Filter out invalid ratings
	const ratings = rawRatings.filter((r) => validRatings.includes(+r));
	let rawModels = parameterMap['models[]'] ?? [];
	rawModels = Array.isArray(rawModels) ? rawModels : [rawModels];
	// Filter out invalid models
	const models = rawModels.filter((m) => validModels.includes(m));
	return {
		ratings,
		models,
		types
	};
};

/*
 * Returns true if the parameter map has a filter
 * @param {QueryObject} parameterMap - Object of queries
 * @returns {boolean} - true if the parameter map has a filter
 */
export const parameterMapHasFilters = (parameterMap) => {
	return Boolean(
		parameterMap['ratings[]'] ||
			parameterMap['models[]'] ||
			(parameterMap.types && parameterMap.types !== 'overall')
	);
};
